import { AUTH_ME_FAILURE_MASK_MAX_MS } from '@/constants';
import { hasAnyOrgRole } from '@/helpers/roles';
import { useAppStore } from '@/stores/app';
import { useUnauthorizedReferrerStore } from '@/stores/unauthorizedReferrerStore';
import type { Role } from '@/types';
import { isForbiddenError } from '@/types/errors';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useAuthStatus } from './useAuthStatus';

export interface RouteProtectionResult {
  /** The effective user (current, or the last authenticated one during a transient /auth/me
   *  failure) holds one of requiredRoles in the selected/stub org. */
  hasAccess: boolean;
  /** Auth status is still PENDING — no access decision can be made yet. */
  isResolving: boolean;
  /** An /auth/me failure this hook will not mask: no established session behind it, a 403
   *  (authoritative), or a mask that outlived its bound. Distinct from a live transient refetch
   *  error, which keeps the last-known user and must not read as terminal. */
  isAuthError: boolean;
  /** Auth resolved to no session at all, cleanly — a 401 or a never-logged-in cold load, with no
   *  /auth/me error behind it. Mutually exclusive with isAuthError, and distinct from a resolved
   *  role/org denial, which has a real user this hook then redirects to `redirectTo`. */
  isUnauthenticated: boolean;
}

/**
 * Single source of truth for the org-admin route-access predicate and the redirect that follows a
 * denial: switching to a role or org without the required role redirects in place. Page components
 * needing a render gate should prefer `useOrgAdminRenderGate`, which wraps this.
 *
 * @param requiredRoles - Array of roles required to access the route
 * @param redirectTo - Path to redirect to if access is lost (default: '/unauthorized')
 *
 * @example
 * ```tsx
 * import { useRouteProtection } from '@/hooks';
 * import { ROLES } from '@/types';
 *
 * function UsersPage() {
 *   // Protect this route - will redirect if role changes
 *   useRouteProtection([ROLES[0]]); // ROLES[0] = 'super_user'
 *
 *   // rest of component
 * }
 * ```
 */
export function useRouteProtection(requiredRoles: Role[], redirectTo = '/unauthorized'): RouteProtectionResult {
  const navigate = useNavigate();
  const router = useRouter();
  const { status, user, userQuery } = useAuthStatus();
  // selectedOrganization is not persisted; fall back to selectedOrgStub (persisted) on hard
  // refresh so the hook doesn't redirect before the full org object is restored.
  const effectiveOrg = useAppStore(useShallow(state => state.selectedOrganization ?? state.selectedOrgStub));

  // useAuthStatus reports a failed /auth/me as UNAUTHENTICATED with `user: null`, discarding the
  // data query-core still holds through a background error. Reading that back is what keeps a
  // transient 5xx from revoking an established session, and because it lives in the shared cache
  // it survives a remount or route change, which a per-hook latch would not. A real 401 resolves
  // to `null` data, so a genuine sign-out clears it on its own.
  const lastAuthenticatedUser = userQuery.data ?? null;

  // A 403 is the server saying this session is no longer allowed — authoritative, not transient,
  // so it must never be masked. Only /auth/me's other non-401 failures (5xx, network) qualify.
  const masking =
    status === 'UNAUTHENTICATED' &&
    userQuery.isError &&
    lastAuthenticatedUser !== null &&
    !isForbiddenError(userQuery.error);

  // Bounded, because nothing else lifts the mask: /auth/me re-errors on its own interval forever,
  // so without a deadline an unrecoverable session would hold a stale page indefinitely rather
  // than reaching AuthCheckFailed's retry surface.
  const [maskExpired, setMaskExpired] = useState(false);
  useEffect(() => {
    if (!masking) {
      // oxlint-disable-next-line set-state-in-effect -- resets the latch once the failure clears (useOrgAdminRenderGate pattern)
      setMaskExpired(false);
      return undefined;
    }
    const timer = setTimeout(() => setMaskExpired(true), AUTH_ME_FAILURE_MASK_MAX_MS);
    return () => clearTimeout(timer);
  }, [masking]);

  const isTransientAuthFailure = masking && !maskExpired;
  // Null outside those two cases, so PENDING and a resolved no-session state fall through to no
  // access. A role/org denial keeps its real user — the redirect effect below requires one.
  const effectiveUser = status === 'AUTHENTICATED' ? user : isTransientAuthFailure ? lastAuthenticatedUser : null;

  const isResolving = status === 'PENDING';
  const isAuthError = status === 'UNAUTHENTICATED' && userQuery.isError && !isTransientAuthFailure;
  // Keyed on the absence of an error rather than on `!effectiveUser`, so a masked transient
  // failure (which still has one) and a mask that has since expired both stay auth errors.
  const isUnauthenticated = status === 'UNAUTHENTICATED' && !userQuery.isError;
  // Fail closed on the no-session state rather than relying on effectiveUser already being null
  // there, so a render gate can never rank `authorized` above `unauthenticated`.
  const hasAccess = useMemo(
    () => !isUnauthenticated && hasAnyOrgRole(effectiveUser?.assumed_roles, effectiveOrg?.id, requiredRoles),
    [isUnauthenticated, effectiveUser, effectiveOrg, requiredRoles],
  );

  useEffect(() => {
    // No effective user means the no-session redirect is the root beforeLoad's (/login), not this
    // hook's — which is what isUnauthenticated exposes to callers. PENDING isn't decidable yet.
    if (isResolving || !effectiveUser) return;
    if (hasAccess) return;

    const redirectWithReferrer = async () => {
      try {
        // The store owns the sessionStorage key and writes it itself; the same reason
        // router.history.location is read below applies here, so don't read window.location.
        useUnauthorizedReferrerStore.getState().setReferrerPath(router.history.location.pathname);
      } catch (error) {
        // Losing the referrer must not cost the redirect itself — that's what protects the route.
        console.error('[useRouteProtection] could not record the unauthorized referrer; redirecting anyway', error);
      }
      if (router.history.location.pathname === redirectTo) return;

      try {
        await navigate({ to: redirectTo });
      } catch (error) {
        console.error('[useRouteProtection] guard redirect threw; user left on the current page', error);
        return;
      }
      // router.navigate's drop paths (src/lib/router.tsx) resolve rather than reject, so the
      // try/catch above can never see them (issue #396). router.history.location is the ground
      // truth here: window.location only catches up on TanStack History's deferred flush.
      if (router.history.location.pathname !== redirectTo) {
        console.error(
          `[useRouteProtection] redirect to ${redirectTo} was dropped; still on ${router.history.location.pathname} — forcing a document navigation`,
        );
        // Assumed to be the in-app router guard, which only a real document load bypasses.
        window.location.assign(redirectTo);
      }
    };
    void redirectWithReferrer().catch(error => {
      console.error('[useRouteProtection] guard redirect failed unexpectedly', error);
    });
  }, [hasAccess, isResolving, effectiveUser, redirectTo, navigate, router]);

  return { hasAccess, isResolving, isAuthError, isUnauthenticated };
}
